import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { type RouterInputs, trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Input, Switch } from 'antd';
import { useId } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/handling-parties/add')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'New handling party',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <HandlingPartyAdd />
        </PageController>
    );
}

type HandlingParty = RouterInputs['handlingParty']['createHandlingParty'];

const FormItem = Form.Item<HandlingParty>;

function HandlingPartyAdd() {
    const formId = useId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.handlingParty.createHandlingParty.useMutation();
    const navigate = useNavigate();

    const [form] = Form.useForm<HandlingParty>();

    return (
        <OverlayPage
            title="New handling party"
            afterClose={() => {
                form.resetFields();
                void trpcUtils.handlingParty.invalidate();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose }) => (
                <>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" htmlType="submit" form={formId}>
                        Save
                    </Button>
                </>
            )}
        >
            {({ onClose }) => (
                <Form
                    id={formId}
                    layout="vertical"
                    form={form}
                    onFinish={(values) => {
                        message.loading('Saving...');

                        mutate(values, {
                            onSuccess() {
                                message.success('Saved.');
                                onClose();
                            },
                            onError(error) {
                                if (error.data?.errorId === 'handlingParty.alreadyExists') {
                                    message.destroy();

                                    form.setFields([
                                        {
                                            name: 'name',
                                            errors: ['This name is already in use'],
                                        },
                                    ]);

                                    return;
                                }

                                errorMessage.show(error);
                            },
                        });
                    }}
                >
                    <FormItem label="Name" name="name" rules={[{ required: true }]}>
                        <Input />
                    </FormItem>
                    <FormItem label="Is disabled?" name="isDisabled">
                        <Switch />
                    </FormItem>
                </Form>
            )}
        </OverlayPage>
    );
}
